import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import { PlusOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Modal, Input, Space, Typography, Button, Card, Select, InputNumber, Spin, Tooltip, message } from 'antd';

import { predictionModelsApi } from '../../../../shared/common/api/endpoints/predictionModelsApi';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';
import { RuleBlock, RuleCondition, ConditionAction, ActionType, ModelStatus, LearningStatus, ActiveModel, RuleFilter, FilterValueItem } from '../../types';

const { Text } = Typography;

// ID предложения GetCourse — только цифры
const isOfferIdValid = (offerId?: string) => /^\d+$/.test((offerId || '').trim());

const OFFER_ID_HINT =
  'ID предложения из GetCourse. Где взять: GetCourse → Продажи → Продукты → вкладка «Предложения». ' +
  'ID — число в первой колонке «ID» рядом с названием предложения, например 7296684. ' +
  'Название предложения не подойдёт — только число. Если такого ID в GetCourse нет, заказ не создастся, ' +
  'а у модели появится отметка об ошибке.';

const EXTRA_FIELD_HINT =
  'В это доп. поле ученика в GetCourse запишется его вероятность покупки в процентах, например 87.5. ' +
  'Поле должно уже существовать в GetCourse — укажите его название точно. ' +
  'Значение обновляется, когда процент меняется.';

// Задержка перед запросом при вводе в поиск фильтра, мс
const SEARCH_DEBOUNCE_MS = 300;

const FILTERS_HINT =
  'Фильтр задаёт, какую покупку модель учится предсказывать, а не каких учеников смотреть. ' +
  'Несколько значений внутри одного фильтра — «ИЛИ» (например, тег «Клуб» или «Интенсив»). ' +
  'Разные фильтры — «И» (например, тег «Клуб» и сумма заказа от 5000 ₽). ' +
  'Без фильтров модель предсказывает любую покупку в школе.';

// Сумма заказа: нужна хотя бы одна граница, и «от» меньше «до»
const isSumRangeInvalid = (filter: RuleFilter) => {
  if (filter.type !== 'sum_order') return false;
  const start = filter.rangeValue?.start;
  const end = filter.rangeValue?.end;
  const hasStart = typeof start === 'number';
  const hasEnd = typeof end === 'number';
  if (!hasStart && !hasEnd) return true;
  return hasStart && hasEnd && (start as number) >= (end as number);
};

const uid = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Исключать купивших: 0 — не исключать, -1 — навсегда, иначе срок в днях после покупки.
// Подписи — «N дней после покупки»: «Купил за последние 30 дней» читалось как «брать купивших»
const EXCLUDE_BOUGHT_OPTIONS = [
  { value: 0, label: 'Не исключать купивших' },
  { value: 30, label: '30 дней после покупки' },
  { value: 90, label: '90 дней после покупки' },
  { value: 180, label: '180 дней после покупки' },
  { value: 365, label: 'Год после покупки' },
  { value: -1, label: 'Навсегда — купил хоть раз' },
];

interface RuleFormModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (rule: RuleBlock) => Promise<void>;
  editingRule?: RuleBlock & { isActive?: boolean; createdAt?: string; lastChecked?: string };
  dictionaries?: {
    actionTypes: ActionType[];
    modelStatuses: ModelStatus[];
    learningStatuses: LearningStatus[];
    activeModels: ActiveModel[];
  };
}

export const RuleFormModal: React.FC<RuleFormModalProps> = ({
  open,
  onCancel,
  onSave,
  editingRule,
  dictionaries,
}) => {
  // Get selected school
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);

  // Filter values state
  const [filterValues, setFilterValues] = useState<{ [key: string]: FilterValueItem[] }>({});
  const [filterValuesLoading, setFilterValuesLoading] = useState<{ [key: string]: boolean }>({});
  // Таймеры поиска по фильтрам: запрос уходит, когда пользователь перестал печатать
  const searchTimers = useRef<{ [filterId: string]: ReturnType<typeof setTimeout> }>({});
  const [ruleBlock, setRuleBlock] = useState<RuleBlock>(() => ({
    id: uid(),
    name: '',
    excludeBoughtDays: 0,
    periodDays: 5,
    groups: [{
      id: uid(),
      logic: 'OR',
      atoms: [{
        id: uid(),
        metric: 'unique_reg_count',
        comparator: '>',
        value: 0,
        periodDays: 5
      }]
    }],
    conditions: [],
    actions: {
      budgetAction: 'none',
      budgetAmount: 0,
      budgetUnit: 'rub',
      sendTelegram: false,
      tgMessage: '',
      tgRecipients: []
    },
    filters: []
  }));

  useEffect(() => {
    if (editingRule) {
      setRuleBlock({
        ...editingRule,
        conditions: editingRule.conditions || []
      });

      // Предзагружаем данные для фильтров, которые уже имеют selectedItems
      editingRule.filters?.forEach(filter => {
        if (filter.selectedItems && filter.selectedItems.length > 0) {
          setFilterValues(prev => ({ ...prev, [filter.id]: filter.selectedItems! }));
        }
      });
    } else {
      setRuleBlock({
        id: uid(),
        name: '',
        excludeBoughtDays: 0,
        periodDays: 5,
        groups: [{
          id: uid(),
          logic: 'OR',
          atoms: [{
            id: uid(),
            metric: 'unique_reg_count',
            comparator: '>',
            value: 0,
            periodDays: 5
          }]
        }],
        conditions: [],
        actions: {
          budgetAction: 'none',
          budgetAmount: 0,
          budgetUnit: 'rub',
          sendTelegram: false,
          tgMessage: '',
          tgRecipients: []
        },
        filters: []
      });
    }
  }, [editingRule, open]);

  const addCondition = () => {
    const newCondition: RuleCondition = {
      id: uid(),
      probabilityFrom: 50,
      probabilityTo: 100,
      actions: [{
        id: uid(),
        type: 1
      }]
    };
    setRuleBlock(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  };

  const removeCondition = (conditionId: string) => {
    setRuleBlock(prev => ({
      ...prev,
      conditions: prev.conditions.filter(c => c.id !== conditionId)
    }));
  };

  const updateCondition = (conditionId: string, updates: Partial<RuleCondition>) => {
    setRuleBlock(prev => ({
      ...prev,
      conditions: prev.conditions.map(condition =>
        condition.id === conditionId ? { ...condition, ...updates } : condition
      )
    }));
  };

  const addActionToCondition = (conditionId: string) => {
    const newAction: ConditionAction = {
      id: uid(),
      type: 1
    };
    setRuleBlock(prev => ({
      ...prev,
      conditions: prev.conditions.map(condition =>
        condition.id === conditionId
          ? { ...condition, actions: [...condition.actions, newAction] }
          : condition
      )
    }));
  };

  // Helper function to get available filter types
  const getAvailableFilterTypes = () => {
    const allTypes: ('list_tags' | 'list_sentence' | 'list_tags_3' | 'sum_order')[] = ['list_tags', 'list_sentence', 'list_tags_3', 'sum_order'];
    const usedTypes = ruleBlock.filters.map(filter => filter.type);
    return allTypes.filter(type => !usedTypes.includes(type as any));
  };

  // Helper function to get filter type name
  const getFilterTypeName = (type: string) => {
    const typeNames: { [key: string]: string } = {
      'list_tags': 'Тег предложения',
      'list_sentence': 'Название предложения',
      'list_tags_3': 'Тег заказа',
      'sum_order': 'Сумма заказа от и до'
    };
    return typeNames[type] || type;
  };

  const addFilter = () => {
    const availableTypes = getAvailableFilterTypes();

    if (availableTypes.length === 0) {
      message.error('Все типы фильтров уже добавлены. Удалите один из существующих фильтров, чтобы добавить новый.');
      return;
    }

    const firstAvailableType = availableTypes[0];
    const newFilter: RuleFilter = {
      id: uid(),
      type: firstAvailableType as any,
      name: getFilterTypeName(firstAvailableType),
      selectedIds: firstAvailableType === 'sum_order' ? undefined : [],
      rangeValue: firstAvailableType === 'sum_order' ? {} : undefined
    };

    setRuleBlock(prev => ({
      ...prev,
      filters: [...prev.filters, newFilter]
    }));
  };

  const removeFilter = (filterId: string) => {
    setRuleBlock(prev => ({
      ...prev,
      filters: prev.filters.filter(filter => filter.id !== filterId)
    }));
  };

  const updateFilter = (filterId: string, updates: Partial<RuleFilter>) => {
    setRuleBlock(prev => ({
      ...prev,
      filters: prev.filters.map(filter =>
        filter.id === filterId ? { ...filter, ...updates } : filter
      )
    }));
  };

  // Load filter values from API
  const loadFilterValues = async (filterId: string, filterType: string, query = '') => {
    if (!selectedSchool?.id) {
      console.error('No selected school');
      return;
    }

    setFilterValuesLoading(prev => ({ ...prev, [filterId]: true }));

    try {
      const filterNameMap: { [key: string]: string } = {
        'list_tags': 'list_tags',
        'list_sentence': 'list_sentence',
        'list_tags_3': 'list_tags_3',
        'sum_order': 'uoe_sum_order'
      };

      const requestPayload = {
        school_id: Number(selectedSchool.id),
        name: filterNameMap[filterType] || filterType,
        str: query || undefined
      };

      const response = await predictionModelsApi.fetchFilterValues(requestPayload);

      if (response.status === 200) {
        setFilterValues(prev => ({ ...prev, [filterId]: response.data }));
      }
    } catch (error) {
      console.error('Error loading filter values:', error);
    } finally {
      setFilterValuesLoading(prev => ({ ...prev, [filterId]: false }));
    }
  };

  // Handle filter type change
  const handleFilterTypeChange = (filterId: string, newType: string) => {
    // Check if the new type is already used by another filter
    const isTypeAlreadyUsed = ruleBlock.filters.some(filter =>
      filter.id !== filterId && filter.type === newType
    );

    if (isTypeAlreadyUsed) {
      const typeName = getFilterTypeName(newType);
      message.error(`Тип фильтра "${typeName}" уже используется в другом блоке. Выберите другой тип.`);
      return;
    }

    updateFilter(filterId, {
      type: newType as any,
      name: getFilterTypeName(newType),
      selectedIds: newType === 'sum_order' ? undefined : [],
      rangeValue: newType === 'sum_order' ? {} : undefined
    });

    // Сбрасываем состояние загруженных значений для этого фильтра
    setFilterValues(prev => ({ ...prev, [filterId]: [] }));
    setFilterValuesLoading(prev => ({ ...prev, [filterId]: false }));

    // Load values for non-range filters
    if (newType !== 'sum_order') {
      loadFilterValues(filterId, newType);
    }
  };

  const removeActionFromCondition = (conditionId: string, actionId: string) => {
    setRuleBlock(prev => ({
      ...prev,
      conditions: prev.conditions.map(condition =>
        condition.id === conditionId
          ? { ...condition, actions: condition.actions.filter(a => a.id !== actionId) }
          : condition
      )
    }));
  };

  const updateActionInCondition = (conditionId: string, actionId: string, updates: Partial<ConditionAction>) => {
    setRuleBlock(prev => ({
      ...prev,
      conditions: prev.conditions.map(condition =>
        condition.id === conditionId
          ? {
            ...condition,
            actions: condition.actions.map(action =>
              action.id === actionId ? { ...action, ...updates } : action
            )
          }
          : condition
      )
    }));
  };

  const handleSave = async () => {
    if (!ruleBlock.name.trim()) {
      return;
    }

    const validConditions = (ruleBlock.conditions || []).filter(c => c.actions.length > 0);

    // Проверяем, что есть хотя бы одно условие
    if (validConditions.length === 0) {
      message.error('Должно быть указано минимум одно условие');
      return;
    }

    try {
      await onSave({
        ...ruleBlock,
        conditions: validConditions
      });
      // Закрываем модалку только при успешном сохранении
      onCancel();
    } catch (error: any) {
      // При ошибке модалка остается открытой
      console.error('Error saving rule:', error);

      // Показываем пользователю понятное сообщение об ошибке.
      // На 400 и 422 сервер присылает причину по-русски — показываем её
      const serverMessage: string | undefined = error.response?.data?.message;
      if (error.response?.status === 400) {
        message.error(serverMessage || 'Неверный формат данных');
      } else if (error.response?.status === 401) {
        message.error('Не авторизован');
      } else if (error.response?.status === 403) {
        message.error('Доступ запрещен');
      } else if (error.response?.status === 404) {
        message.error('Модель не найдена');
      } else if (error.response?.status === 422) {
        message.error(serverMessage || 'Модель с таким названием уже существует');
      } else if (error.response?.status === 500) {
        message.error('Не удалось сохранить модель: ошибка на сервере. Попробуйте ещё раз или напишите в поддержку.');
      } else {
        message.error(error.message || 'Ошибка при сохранении модели');
      }
    }
  };

  const hasConditionsWithoutActions = (ruleBlock.conditions || []).some(c => c.actions.length === 0);

  // «От» больше «до» — условие не сработает никогда
  const isRangeInvalid = (condition: RuleCondition) => condition.probabilityFrom > condition.probabilityTo;

  // Номера других условий (с 1), чьи диапазоны пересекаются с этим, — границы включительно
  const getOverlappingConditionNumbers = (condition: RuleCondition): number[] =>
    (ruleBlock.conditions || []).reduce<number[]>((numbers, other, index) => {
      if (
        other.id !== condition.id &&
        !isRangeInvalid(other) &&
        !isRangeInvalid(condition) &&
        condition.probabilityFrom <= other.probabilityTo &&
        other.probabilityFrom <= condition.probabilityTo
      ) {
        numbers.push(index + 1);
      }
      return numbers;
    }, []);

  const hasInvalidRanges = (ruleBlock.conditions || []).some(isRangeInvalid);

  const hasActionsWithEmptyFields = (ruleBlock.conditions || []).some(condition =>
    condition.actions.some(action => {
      if (action.type === 1) {
        return !action.groupName || action.groupName.trim() === '';
      }
      if (action.type === 2) {
        return !isOfferIdValid(action.offerId);
      }
      if (action.type === 3) {
        return !action.fieldName || action.fieldName.trim() === '';
      }
      return false;
    })
  );

  const hasInvalidSumRange = ruleBlock.filters.some(isSumRangeInvalid);

  const isFormValid = ruleBlock.name.trim()
    && !hasConditionsWithoutActions
    && !hasActionsWithEmptyFields
    && !hasInvalidRanges
    && !hasInvalidSumRange;

  // Названия выбранных значений фильтра — из загруженного списка или сохранённых в модели
  const getSelectedNames = (filter: RuleFilter): string[] => {
    const known = [...(filterValues[filter.id] || []), ...(filter.selectedItems || [])];
    return (filter.selectedIds || []).map(id => {
      const item = known.find(value => value.id === id);
      return item ? item.name.replace(/&nbsp;/g, ' ') : `№${id}`;
    });
  };

  // Фраза-итог под фильтрами: «Предсказываем покупку: Клуб ИЛИ Интенсив, И сумма заказа от 5000 ₽»
  const filtersSummary = (() => {
    const parts = ruleBlock.filters.map(filter => {
      if (filter.type === 'sum_order') {
        const start = filter.rangeValue?.start;
        const end = filter.rangeValue?.end;
        if (typeof start === 'number' && typeof end === 'number') return `сумма заказа от ${start} до ${end} ₽`;
        if (typeof start === 'number') return `сумма заказа от ${start} ₽`;
        if (typeof end === 'number') return `сумма заказа до ${end} ₽`;
        return '';
      }
      const names = getSelectedNames(filter);
      if (names.length === 0) return '';
      return `${filter.name.toLowerCase()}: ${names.join(' ИЛИ ')}`;
    }).filter(Boolean);

    return parts.length ? `Предсказываем покупку: ${parts.join(', И ')}` : '';
  })();

  return (
    <Modal
      visible={open}
      onCancel={onCancel}
      onOk={handleSave}
      width={700}
      title={null}
      cancelText="Отмена"
      okText={editingRule ? 'Сохранить' : 'Создать'}
      okButtonProps={{
        disabled: !isFormValid,
        style: isFormValid
          ? { backgroundColor: '#1677ff', borderColor: '#1677ff' }
          : { backgroundColor: '#d9d9d9', borderColor: '#d9d9d9', color: '#00000040' }
      }}
    >
      <Space direction="vertical" size={24} style={{ width: '100%', marginTop: 24 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text strong>Название модели</Text>
          <Input
            placeholder="Например: Высокая вероятность покупки"
            value={ruleBlock.name}
            onChange={(e) => setRuleBlock(prev => ({ ...prev, name: e.target.value }))}
            size="large"
          />
        </Space>

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text strong>Исключать купивших</Text>
          <Select
            value={ruleBlock.excludeBoughtDays ?? 0}
            onChange={(value) => setRuleBlock(prev => ({ ...prev, excludeBoughtDays: value }))}
            style={{ width: '100%' }}
            options={EXCLUDE_BOUGHT_OPTIONS}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Купил то, что предсказывает модель, — в течение выбранного срока ему ничего не выполняется
            и в «Результатах предсказания» его не видно. Срок считается от последней оплаты;
            потом, если не купил снова, ученик опять попадает в список.
            Для подписки на месяц — 30 дней, для разового курса — «навсегда».
          </Text>
        </Space>

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong>Какую покупку предсказываем</Text>
            <Tooltip title={FILTERS_HINT}>
              <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addFilter}
              shape="circle"
              size="small"
            />
          </div>
          {ruleBlock.filters.length === 0 && (
            <Text type="warning" style={{ fontSize: 12 }}>
              Фильтр не задан — модель будет предсказывать любую покупку в школе.
              Чтобы предсказывать конкретный продукт, добавьте фильтр кнопкой «+».
            </Text>
          )}
        </Space>

        {/* Filters Section */}
        {ruleBlock.filters.length > 0 && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {ruleBlock.filters.map((filter) => (
              <Card
                key={filter.id}
                style={{
                  backgroundColor: '#fafafa',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px'
                }}
                size="small"
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong>Фильтр</Text>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeFilter(filter.id)}
                      size="small"
                    />
                  </div>

                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text>Тип фильтра:</Text>
                    <Select
                      value={filter.type}
                      onChange={(value) => handleFilterTypeChange(filter.id, value)}
                      style={{ width: '100%' }}
                    >
                      {getAvailableFilterTypes().concat(filter.type).map(type => (
                        <Select.Option key={type} value={type}>
                          {getFilterTypeName(type)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Space>

                  {/* Range filter UI for sum_order */}
                  {filter.type === 'sum_order' && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Text>Диапазон суммы заказа:</Text>
                      <Space>
                        <InputNumber
                          placeholder="От"
                          value={filter.rangeValue?.start}
                          onChange={(value) => updateFilter(filter.id, {
                            rangeValue: {
                              ...filter.rangeValue,
                              start: typeof value === 'number' ? value : undefined
                            }
                          })}
                          status={isSumRangeInvalid(filter) ? 'error' : undefined}
                          style={{ width: 120 }}
                          min={0}
                        />
                        <Text>—</Text>
                        <InputNumber
                          placeholder="До"
                          value={filter.rangeValue?.end}
                          onChange={(value) => updateFilter(filter.id, {
                            rangeValue: {
                              ...filter.rangeValue,
                              end: typeof value === 'number' ? value : undefined
                            }
                          })}
                          status={isSumRangeInvalid(filter) ? 'error' : undefined}
                          style={{ width: 120 }}
                          min={0}
                        />
                      </Space>
                      {isSumRangeInvalid(filter) && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          Укажите хотя бы одну границу суммы; «от» должно быть меньше «до».
                        </Text>
                      )}
                    </Space>
                  )}

                  {/* Integrated Search/Select UI for list filters */}
                  {filter.type !== 'sum_order' && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Text>Поиск и выбор:</Text>
                      <Select
                        mode="multiple"
                        placeholder={`Поиск и выбор ${filter.name.toLowerCase()}...`}
                        value={filter.selectedIds}
                        onChange={(value) => updateFilter(filter.id, { selectedIds: value })}
                        style={{ width: '100%' }}
                        allowClear
                        showSearch
                        loading={filterValuesLoading[filter.id]}
                        notFoundContent={filterValuesLoading[filter.id] ? <Spin size="small" /> : 'Ничего не найдено'}
                        onSearch={(value) => {
                          // Запрос — только когда пользователь перестал печатать
                          clearTimeout(searchTimers.current[filter.id]);
                          searchTimers.current[filter.id] = setTimeout(() => {
                            loadFilterValues(filter.id, filter.type, value);
                          }, SEARCH_DEBOUNCE_MS);
                        }}
                        onDropdownVisibleChange={(open) => {
                          if (open && !filterValues[filter.id]?.length && !filterValuesLoading[filter.id]) {
                            loadFilterValues(filter.id, filter.type);
                          }
                        }}
                        filterOption={false} // Disable client-side filtering since we use server-side search
                      >
                        {filterValues[filter.id]?.map(item => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name.replace(/&nbsp;/g, ' ')}
                          </Select.Option>
                        ))}
                      </Select>
                    </Space>
                  )}
                </Space>
              </Card>
            ))}
            {filtersSummary && (
              <Text style={{ fontSize: 13, color: '#1677ff' }}>{filtersSummary}</Text>
            )}
          </Space>
        )}

        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {(ruleBlock.conditions || []).length === 0 ? (
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={addCondition}
              style={{ alignSelf: 'flex-start' }}
            >
              Добавить условие и действие
            </Button>
          ) : (
            <>
              {(ruleBlock.conditions || []).map((condition) => (
                <Card
                  key={condition.id}
                  style={{
                    backgroundColor: '#fafafa',
                    border: '1px solid #e8e8e8'
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap' }}>
                        <Text>Если у пользователя вероятность покупки от</Text>
                        <InputNumber
                          value={condition.probabilityFrom}
                          onChange={(value) => {
                            // Пустое поле ничем не подменяем — остаётся прежнее значение
                            if (typeof value === 'number') updateCondition(condition.id, { probabilityFrom: value });
                          }}
                          min={0}
                          max={100}
                          status={isRangeInvalid(condition) ? 'error' : undefined}
                          formatter={(value) => `${value}%`}
                          parser={(value) => Number(value!.replace('%', ''))}
                          style={{ width: 90 }}
                        />
                        <Text>до</Text>
                        <InputNumber
                          value={condition.probabilityTo}
                          onChange={(value) => {
                            if (typeof value === 'number') updateCondition(condition.id, { probabilityTo: value });
                          }}
                          min={0}
                          max={100}
                          status={isRangeInvalid(condition) ? 'error' : undefined}
                          formatter={(value) => `${value}%`}
                          parser={(value) => Number(value!.replace('%', ''))}
                          style={{ width: 90 }}
                        />
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeCondition(condition.id)}
                        size="small"
                      />
                    </div>

                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Действия при срабатывании условия
                      </Text>

                      {condition.actions.map((action) => (
                        <div key={action.id} style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: '#ffffff',
                          borderRadius: 6,
                          border: '1px solid #f0f0f0'
                        }}>
                          <Select
                            value={action.type}
                            onChange={(value) => {
                              // При смене типа действия сбрасываем все поля и устанавливаем только нужные
                              const resetFields = {
                                type: value,
                                offerId: undefined,
                                groupName: undefined,
                                fieldName: undefined
                              };
                              updateActionInCondition(condition.id, action.id, resetFields);
                            }}
                            style={{ width: 280 }}
                          >
                            {dictionaries?.actionTypes.map(actionType => (
                              <Select.Option key={actionType.id} value={actionType.id}>
                                {actionType.name}
                              </Select.Option>
                            ))}
                          </Select>

                          {action.type === 1 && (
                            <Input
                              placeholder="Название группы"
                              value={action.groupName || ''}
                              onChange={(e) => updateActionInCondition(condition.id, action.id, { groupName: e.target.value })}
                              style={{ flex: 1 }}
                            />
                          )}

                          {action.type === 2 && (
                            <>
                              <Input
                                placeholder="ID предложения в GetCourse, например 1234567"
                                value={action.offerId || ''}
                                onChange={(e) => updateActionInCondition(condition.id, action.id, { offerId: e.target.value.trim() })}
                                status={action.offerId && !isOfferIdValid(action.offerId) ? 'error' : undefined}
                                style={{ flex: 1 }}
                              />
                              <Tooltip title={OFFER_ID_HINT}>
                                <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                              </Tooltip>
                            </>
                          )}

                          {action.type === 3 && (
                            <>
                              <Input
                                placeholder="Название доп. поля в GetCourse"
                                value={action.fieldName || ''}
                                onChange={(e) => updateActionInCondition(condition.id, action.id, { fieldName: e.target.value })}
                                style={{ flex: 1 }}
                              />
                              <Tooltip title={EXTRA_FIELD_HINT}>
                                <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                              </Tooltip>
                            </>
                          )}


                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeActionFromCondition(condition.id, action.id)}
                            size="small"
                          />
                        </div>
                      ))}

                      <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={() => addActionToCondition(condition.id)}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        Добавить действие
                      </Button>
                    </Space>

                    {condition.actions.length > 0 && (
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: '#f0f7ff',
                          borderRadius: 6,
                          border: '1px solid #d6e4ff'
                        }}>
                        <Text style={{ fontSize: 13, color: '#1677ff' }}>
                          {(() => {
                            const actionTexts = condition.actions.map(action => {
                              if (action.type === 1) {
                                return action.groupName ? `добавить в группу "${action.groupName}"` : 'добавить в группу';
                              }
                              if (action.type === 2) {
                                return action.offerId ? `создать заказ с предложением "${action.offerId}"` : 'создать заказ с предложением';
                              }
                              if (action.type === 3) {
                                return action.fieldName ? `изменить значение доп. поля "${action.fieldName}"` : 'изменить значение доп. поля';
                              }
                              return '';
                            }).filter(Boolean);

                            return `Если вероятность покупки от ${condition.probabilityFrom} до ${condition.probabilityTo}%, то ${actionTexts.join(', ')}`;
                          })()}
                        </Text>
                      </div>
                    )}

                    {isRangeInvalid(condition) && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        «От» не может быть больше «до».
                      </Text>
                    )}

                    {getOverlappingConditionNumbers(condition).length > 0 && (
                      <Text type="warning" style={{ fontSize: 12 }}>
                        Диапазон пересекается с условием № {getOverlappingConditionNumbers(condition).join(', ')}:
                        ученики на пересечении получат действия обоих условий.
                      </Text>
                    )}
                  </Space>
                </Card>
              ))}
              <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={addCondition}
                style={{ alignSelf: 'flex-start' }}
              >
                Добавить условие и действие
              </Button>
            </>
          )}
        </Space>
      </Space>
    </Modal>
  );
};

